<template>
  <div ref="rootContainer" :class="`pge-dropdown ${isActive ? 'active' : ''}`" @click="handleContainerClick">
    <div class="pge-dropdown__separated-label-text" v-if="labelType === 'separated'">
      {{ label }} <span v-if="required">*</span>
    </div>
    <div class="text-field">
      <div class="text-field">
        <div class="state-layer">
          <div class="content">
            <span v-if="selectedOption" class="content__icon">
              <icon-component :icon="selectedOption.icon" />
            </span>
            <div class="input-text">
              <input :id="inputId" class="input-text" type="text" :required="required" :placeholder="placeholder"
                @blur="handleBlurInput" @focus="handleFocusInput" @input="handleChangeInput"
                @keydown.enter="handleEnterKey" @keydown.down.prevent="moveDown" @keydown.up.prevent="moveUp"
                v-model="searchText" />
            </div>
            <div class="label-text" v-if="labelType === 'floated'">
              <label class="label-text" :for="inputId">{{ label }} <span v-if="required">*</span></label>
            </div>
          </div>
          <span class="pge-icon caret">
            <icon-caret-down :class="isActive ? 'up' : 'down'" />
          </span>
        </div>
      </div>
    </div>
    <div
      :class="`pge-menu ${placement === 'top' ? 'top' : ''}`"
      ref="menuContainer"
      :style="menuMaxHeight ? { maxHeight: menuMaxHeight } : null"
    >
      <div class="text-scrollbar" ref="scrollbar">
        <div class="list-items">
          <div class="list-items-section">
            <div 
              v-for="(option, index) in filteredOptions" 
              :key="option.label"
              :class="{ 'dropdown-menu-item': true, 'pge-highlighted': index === selectedOptionIndex }"
              @mousedown="handleOnClickItem(option)" 
            >
              <span v-if="option.icon" class="dropdown-menu-item__icon">
                <icon-component :icon="option.icon" />
              </span>
              <span v-if="option.img" class="dropdown-menu-item__img">
                <icon-component v-if="option.img === 'Default'" icon="DefaultAvatar" />
                <img v-else width="24" height="24" :src="option.img" :alt="option.label" />
              </span>
              <span class="dropdown-menu-item__label">
                <div class="text-primary">{{ option.label }}</div>
                <div class="text-secondary" v-if="option.info">{{ option.info }}</div>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, useId } from 'vue';
import useClickOutside from '../../composables/useClickOutside';
import { removeWhitespaces } from '../../helpers/helper';
import IconComponent from '../icons/index.vue';
import IconCaretDown from './../../components/icons/IconCaretDown.vue';

export default {
  components: {
    IconComponent,
    IconCaretDown,
  },
  // Owns the template ref + outside-click signal; the Options side watches
  // clickedOutside to close. Keeps the deterministic open/close state in one
  // place instead of the old blur-timeout race.
  setup() {
    const rootContainer = ref(null);
    const clickedOutside = ref(0);
    useClickOutside(rootContainer, () => { clickedOutside.value++; });
    return { rootContainer, clickedOutside, inputId: useId() };
  },
  props: {
    label: {
      type: String,
      default: 'Select'
    },
    labelType: {
      type: String,
      default: 'floated',
      validator: v => ['floated', 'separated'].includes(v)
    },
    placeholder: {
      type: String,
      default: 'Select'
    },
    options: {
      type: Array,
      default: () => []
    },
    modelValue: {
      type: String,
      default: ''
    },
    onChange: Function,
    // 'auto' measures the room actually available when the menu opens; 'top'
    // and 'bottom' force a side. It used to default to 'bottom' and nothing
    // ever set anything else, so a long list always grew downward over
    // whatever sat beneath it.
    position: {
      type: String,
      default: 'auto'
    },
    required: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      searchText: this.options.find(item => removeWhitespaces(item.value) === removeWhitespaces(this.modelValue))?.label,
      isActive: false,
      placement: 'bottom',
      menuMaxHeight: null,
      isTyping: false,
      selectedOption: this.options.find(item => removeWhitespaces(item.value) === removeWhitespaces(this.modelValue)) || null,
      selectedOptionIndex: -1,
    };
  },
  computed: {
    filteredOptions() {
      if (this.isActive && !this.isTyping) {
        return this.options;
      } else {
        const searchRegex = new RegExp(this.searchText?.trim(), 'i');
        return this.options.filter(option => searchRegex.test(option.label));
      }
    }
  },
  watch: {
    modelValue(value) {
      this.searchText = this.options.find(item => item.value === value)?.label;
      this.selectedOption = this.options.find(item => item.value === value);
    },
    options(newOptions) {
      this.searchText = newOptions.find(item => item.value === this.modelValue)?.label;
      this.selectedOption = newOptions.find(item => item.value === this.modelValue);
    },
    clickedOutside() {
      if (this.isActive) this.closeMenu();
    }
  },
  methods: {
    handleFocusInput() {
      this.isActive = true;
      this.selectedOptionIndex = -1;
      this.$nextTick(() => this.placeMenu());
    },
    /**
     * Open into the space that exists rather than always downward, and never
     * grow past it. A list of templates is otherwise unbounded by anything but
     * its own item count, and covers the controls below it.
     */
    placeMenu() {
      const root = this.$refs.rootContainer;
      const menu = this.$refs.menuContainer;
      if (!root || !menu) return;

      const GAP = 10;
      const EDGE = 12;
      const CAP = 255;

      const box = root.getBoundingClientRect();
      const below = window.innerHeight - box.bottom - GAP - EDGE;
      const above = box.top - GAP - EDGE;

      if (this.position === 'top' || this.position === 'bottom') {
        this.placement = this.position;
      } else {
        const wanted = Math.min(CAP, menu.scrollHeight);
        this.placement = below < wanted && above > below ? 'top' : 'bottom';
      }

      const room = this.placement === 'top' ? above : below;
      // A floor, so a control wedged against an edge still shows a usable list
      // and scrolls rather than collapsing to nothing.
      this.menuMaxHeight = `${Math.round(Math.max(120, Math.min(CAP, room)))}px`;

      if (this.placement === 'top') {
        menu.style.top = '';
        menu.style.bottom = `${root.clientHeight}px`;
      } else {
        menu.style.bottom = '';
        menu.style.top = `${root.clientHeight}px`;
      }
    },
    closeMenu() {
      // Revert free-typed text that doesn't match an option
      const option = this.options.find(item => item.label === this.searchText);
      if (!option) {
        const prevValue = this.options.find(item => removeWhitespaces(item.value) === removeWhitespaces(this.modelValue));
        this.selectedOption = prevValue;
        this.$emit('update:modelValue', prevValue?.value);
        this.searchText = prevValue?.label;
      }

      this.isActive = false;
      this.isTyping = false;
      this.selectedOptionIndex = -1;
      this.$refs.rootContainer?.querySelector('input.input-text')?.blur();
    },
    handleBlurInput(e) {
      // Keyboard tab-away (mouse interactions are handled deterministically
      // by the container/caret/outside-click paths — no timers).
      if (e.relatedTarget && !this.$refs.rootContainer?.contains(e.relatedTarget)) {
        this.closeMenu();
      }
    },
    handleChangeInput(e) {
      this.isTyping = true;
      this.searchText = e.target.value;
    },
    handleOnClickItem(option) {
      this.selectedOption = option;
      this.searchText = option.label;
      this.$emit('update:modelValue', option.value);
      this.$emit('change');
      this.closeMenu();
    },
    handleContainerClick(e) {
      // Picking an option closes the menu on mousedown, which hides the item the
      // press started on, so the browser dispatches that press's click on this
      // container instead — reopening the menu unless it is ignored here.
      if (!e.target.closest('.text-field, .pge-dropdown__separated-label-text')) return;

      if (!this.isActive) {
        this.$refs.rootContainer.querySelector('input.input-text').focus();
        return;
      }
      // Open: only the caret toggles closed; clicks elsewhere in the field
      // keep the menu open (typing/searching).
      if (e.target.closest('.pge-icon.caret')) {
        this.closeMenu();
      }
    },
    handleKeyDown(e) {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.selectedOptionIndex = Math.max(this.selectedOptionIndex - 1, 0);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.selectedOptionIndex = Math.min(this.selectedOptionIndex + 1, this.filteredOptions.length - 1);
      }
    },
    handleEnterKey() {
      if (this.selectedOptionIndex !== -1) {
        const option = this.filteredOptions[this.selectedOptionIndex];
        if (option) {
          this.searchText = option.label;
          this.$emit('update:modelValue', option.value);
          this.$emit('change');
          this.closeMenu();
        }
      }
    },
    moveDown() {
      if (this.selectedOptionIndex < this.filteredOptions.length - 1) {
        this.selectedOptionIndex++;
        this.scrollIntoView();
      }
    },
    moveUp() {
      if (this.selectedOptionIndex > 0) {
        this.selectedOptionIndex--;
        this.scrollIntoView();
      }
    },
    scrollIntoView() {
      const menuContainer = this.$refs.menuContainer;
      const scrollbar = this.$refs.scrollbar;
      const selectedItem = scrollbar.querySelectorAll('.dropdown-menu-item')[this.selectedOptionIndex];

      if (selectedItem) {
        const topOffset = selectedItem.offsetTop;
        const bottomOffset = topOffset + selectedItem.offsetHeight;

        if (bottomOffset > menuContainer.scrollTop + menuContainer.clientHeight) {
          menuContainer.scrollTop = bottomOffset - menuContainer.clientHeight;
        } else if (topOffset < menuContainer.scrollTop) {
          menuContainer.scrollTop = topOffset;
        }
      }
    },
  }
};
</script>

<style>
@import url("./../styles/pge-dropdown/dropdown/index.scss");
@import url("./../styles/pge-dropdown/dropdown/active.scss");
</style>